import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { WarrantyStatus } from './WarrantyStatus';
import { ApexReference } from './ApexReference';
import { JobContactFields } from './JobContactFields';

export default function AppointmentFormModal({ appointment, users, onSuccess, onCancel, staffLoading = false, staffError = '', onRetryStaff }) {
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_mobile: '',
    assigned_to_user_id: '',
    job_type: 'Installation',
    work_type: 'onsite',
    priority: 'Medium',
    status: 'Open',
    appointment_date: '',
    start_time: '',
    end_time: '',
    location: '',
    description: '',
    notes: '',
    estimated_hours: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (appointment) {
      setFormData({
        customer_name: appointment.customer_name || '',
        customer_email: appointment.customer_email || '',
        customer_mobile: appointment.customer_mobile || '',
        assigned_to_user_id: appointment.assigned_to_user_id || '',
        job_type: appointment.job_type || 'Installation',
        work_type: appointment.work_type || 'onsite',
        priority: appointment.priority || 'Medium',
        status: appointment.status || 'Open',
        appointment_date: appointment.appointment_date || '',
        start_time: appointment.start_time || '',
        end_time: appointment.end_time || '',
        location: appointment.location || '',
        description: appointment.description || '',
        notes: appointment.notes || '',
        estimated_hours: appointment.estimated_hours || '',
      });
    }
  }, [appointment]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (staffLoading || staffError) return;
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      const payload = { ...formData, estimated_hours: formData.estimated_hours === '' ? null : Number(formData.estimated_hours) };
      if (appointment) {
        for (const key of ['customer_email', 'customer_mobile']) {
          if (formData[key] === (appointment[key] || '')) delete payload[key];
        }
      }

      if (appointment) {
        await axios.put(`${API}/appointments/${appointment.id}`, payload, { headers });
        toast.success('Appointment updated!');
      } else {
        await axios.post(`${API}/appointments`, payload, { headers });
        toast.success('Appointment created!');
      }
      onSuccess();
    } catch (error) {
      console.error('Appointment submission error:', error);
      console.error('Error response:', error.response?.data);
      
      // Handle different error types
      let errorMessage = 'Operation failed';
      
      if (error.response?.status === 409) {
        // Conflict error with availability
        const detail = error.response.data?.detail;
        if (typeof detail === 'object') {
          errorMessage = detail.message || 'Time slot not available';
          if (detail.conflicts && detail.conflicts.length > 0) {
            errorMessage += ': ' + detail.conflicts.join(', ');
          }
        } else {
          errorMessage = detail || 'Time slot not available';
        }
      } else if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail);
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="appointment-form-modal"
      className="fixed inset-0 flex items-center justify-center p-4 z-50 appointment-editor"
      style={{ background: 'rgba(0, 0, 0, 0.95)', overflowY: 'auto' }}
      onClick={onCancel}
    >
      <div
        className="retro-panel w-full max-w-3xl my-8"
        style={{ maxHeight: '90vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        {appointment && <div className="mb-4"><ApexReference job={appointment} scope="edit" /></div>}
        <h2 data-testid="appointment-form-title" className="text-base md:text-lg font-bold mb-6 glow-text sticky top-0 bg-black pb-4" style={{ color: '#FF6B00', fontFamily: 'Orbitron, sans-serif', zIndex: 10 }}>
          {appointment ? '[EDIT JOB]' : '[CREATE JOB]'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {staffLoading && <p data-testid="appointment-staff-loading" className="text-white/70">Loading staff…</p>}
          {staffError && <div role="alert" data-testid="appointment-staff-error" className="text-red-300">{staffError}<button type="button" data-testid="appointment-staff-retry" className="retro-btn ml-2" onClick={onRetryStaff}>Retry</button></div>}
          {appointment && <WarrantyStatus appointment={appointment} scope="appointment-form" />}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="w-full">
              <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
                Customer Name:
              </label>
              <input
                type="text"
                name="customer_name"
                data-testid="appointment-customer-input"
                disabled={Boolean(appointment?.warranty_parent_id || appointment?.warranty_started_at)}
                value={formData.customer_name}
                onChange={handleChange}
                className="retro-input w-full"
                required
              />
            </div>

            <div className="w-full">
              <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
                {appointment ? 'Reassign:' : 'Assign To:'}
              </label>
              <select
                name="assigned_to_user_id"
                data-testid="appointment-assignee-select"
                value={formData.assigned_to_user_id}
                onChange={handleChange}
                className="retro-input w-full"
                required
              >
                <option value="">Select User...</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.username}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <JobContactFields email={formData.customer_email} mobile={formData.customer_mobile} onChange={handleChange} />
            </div>

            <div className="w-full">
              <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
                Job Type:
              </label>
              <select
                name="job_type"
                data-testid="appointment-jobtype-select"
                disabled={Boolean(appointment?.warranty_parent_id || appointment?.warranty_started_at)}
                value={formData.job_type}
                onChange={handleChange}
                className="retro-input w-full"
                required
              >
                <option value="Installation">Installation</option>
                <option value="New Desktop Rollout">New Desktop Rollout</option>
                {appointment?.warranty_parent_id && <option value="Warranty Ticket">Warranty Ticket</option>}
                <option value="Repair">Repair</option>
                <option value="Maintenance">Maintenance</option>
                <option value="Inspection">Inspection</option>
                <option value="Consultation">Consultation</option>
              </select>
            </div>

            <div className="w-full">
              <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
                Work Type:
              </label>
              <select
                name="work_type"
                data-testid="appointment-worktype-select"
                value={formData.work_type}
                onChange={handleChange}
                className="retro-input w-full"
                required
              >
                <option value="onsite">On-site</option>
                <option value="remote">Remote</option>
                <option value="offsite">Offsite (No time limits)</option>
              </select>
            </div>

            <div className="w-full">
              <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
                Priority:
              </label>
              <select
                name="priority"
                data-testid="appointment-priority-select"
                value={formData.priority}
                onChange={handleChange}
                className="retro-input w-full"
                required
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Urgent">Urgent</option>
              </select>
            </div>

            <div className="w-full">
              <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
                Status:
              </label>
              <select
                name="status"
                data-testid="appointment-status-select"
                value={formData.status}
                onChange={handleChange}
                className="retro-input w-full"
                required
              >
                <option value="Open">Open</option>
                <option value="Scheduled">Scheduled</option>
                <option value="In Progress">In Progress</option>
                <option value="Waiting for Customer">Waiting for Customer</option>
                <option value="Waiting for Parts">Waiting for Parts</option>
                <option value="Completed">Completed</option>
                <option value="Closed">Closed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>

            <div className="w-full">
              <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
                Date:
              </label>
              <input
                type="date"
                name="appointment_date"
                data-testid="appointment-date-input"
                value={formData.appointment_date}
                onChange={handleChange}
                className="retro-input w-full"
                required
              />
            </div>

            <div className="w-full">
              <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
                Start Time:
              </label>
              <input
                type="time"
                name="start_time"
                data-testid="appointment-starttime-input"
                value={formData.start_time}
                onChange={handleChange}
                className="retro-input w-full"
                required
              />
            </div>

            <div className="w-full">
              <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
                End Time:
              </label>
              <input
                type="time"
                name="end_time"
                data-testid="appointment-endtime-input"
                value={formData.end_time}
                onChange={handleChange}
                className="retro-input w-full"
                required
              />
            </div>

            <div className="w-full">
              <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
                {appointment?.warranty_parent_id ? 'Non-billable warranty' : 'Estimated Billable Hours (optional):'}
              </label>
              <input
                type="number"
                name="estimated_hours"
                data-testid="appointment-estimated-hours"
                disabled={Boolean(appointment?.warranty_parent_id)}
                step="0.25"
                min="0"
                value={formData.estimated_hours}
                onChange={handleChange}
                className="retro-input w-full"
                placeholder="e.g., 2.5"
              />
            </div>

            <div className="w-full md:col-span-2">
              <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
                Location:
              </label>
              <input
                type="text"
                name="location"
                data-testid="appointment-location-input"
                value={formData.location}
                onChange={handleChange}
                className="retro-input w-full"
                required
              />
            </div>
          </div>

          <div className="w-full">
            <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
              Description:
            </label>
            <textarea
              name="description"
              data-testid="appointment-description-textarea"
              value={formData.description}
              onChange={handleChange}
              className="retro-input w-full"
              rows="3"
              required
            />
          </div>

          <div className="w-full">
            <label className="block mb-2 text-lg font-semibold" style={{ color: '#FF6B00' }}>
              Office Notes/Updates:
            </label>
            <textarea
              name="notes"
              data-testid="appointment-notes-textarea"
              value={formData.notes}
              onChange={handleChange}
              className="retro-input w-full"
              rows="3"
              placeholder="Internal notes, updates, time changes..."
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-4 pt-4 sticky bottom-0 bg-black pb-4" style={{ zIndex: 10 }}>
            <button
              type="submit"
              data-testid="appointment-submit-button"
              className="retro-btn retro-btn-green flex-1"
              disabled={loading || staffLoading || Boolean(staffError)}
            >
              {loading ? '[PROCESSING...]' : appointment ? '[UPDATE]' : '[CREATE]'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              data-testid="appointment-cancel-button"
              className="retro-btn retro-btn-magenta flex-1"
            >
              [CANCEL]
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
